import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import axiosInstance from "../../../axios/axiosInstance";

interface MemberType {
  id: number;
  value: string;
  label: string;
  is_active: boolean;
  sort_order: number;
}

const MemberTypeManagement: React.FC = () => {
  const [memberTypes, setMemberTypes] = useState<MemberType[]>([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingType, setEditingType] = useState<MemberType | null>(null);
  const [formData, setFormData] = useState({
    value: "",
    label: "",
    is_active: true,
    sort_order: 0,
  });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTypeId, setDeleteTypeId] = useState<number | null>(null);

  useEffect(() => {
    fetchMemberTypes();
  }, []);

  const fetchMemberTypes = async () => {
    setLoading(true);
    try {
      const resp = await axiosInstance.get<{ status: string; data: MemberType[] }>("/api/member-type/all");
      if (resp.status === 200 && resp.data?.status === "ok") {
        setMemberTypes(resp.data.data);
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.body || "Failed to fetch member types");
    } finally {
      setLoading(false);
    }
  };

  // Generate slug from label
  const generateSlug = (label: string): string => {
    return label
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, "-") // Replace non-alphanumeric characters with hyphens
      .replace(/^-+|-+$/g, ""); // Remove leading/trailing hyphens
  };

  const handleCreate = () => {
    setEditingType(null);
    setFormData({
      value: "",
      label: "",
      is_active: true,
      sort_order: 0,
    });
    setShowForm(true);
  };

  const handleEdit = (type: MemberType) => {
    setEditingType(type);
    setFormData({
      value: type.value,
      label: type.label,
      is_active: type.is_active,
      sort_order: type.sort_order,
    });
    setShowForm(true);
  };

  const handleDelete = (id: number) => {
    setDeleteTypeId(id);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!deleteTypeId) return;

    try {
      const resp = await axiosInstance.delete(`/api/member-type/${deleteTypeId}`);
      if (resp.status === 200) {
        toast.success("Member type deleted successfully");
        fetchMemberTypes();
        setShowDeleteModal(false);
        setDeleteTypeId(null);
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.body || "Failed to delete member type");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingType) {
        const resp = await axiosInstance.put(`/api/member-type/${editingType.id}`, formData);
        if (resp.status === 200) {
          toast.success("Member type updated successfully");
          setShowForm(false);
          fetchMemberTypes();
        }
      } else {
        const resp = await axiosInstance.post("/api/member-type", formData);
        if (resp.status === 200) {
          toast.success("Member type created successfully");
          setShowForm(false);
          fetchMemberTypes();
        }
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.body || "Failed to save member type");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Member Types</h2>
        <button
          onClick={handleCreate}
          className="btn-primary flex items-center gap-2 px-4 py-2"
        >
          <i className="fa-solid fa-plus"></i>
          <span>Add Member Type</span>
        </button>
      </div>

      {memberTypes.length === 0 ? (
        <div className="text-center py-20 bg-gray-50 dark:bg-gray-700/30 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600">
          <i className="fa-solid fa-users text-4xl text-gray-400 dark:text-gray-600 mb-3"></i>
          <p className="text-gray-500 dark:text-gray-400 font-medium">No member types found</p>
          <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
            Click "Add Member Type" to create your first member type
          </p>
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden border border-gray-200 dark:border-gray-700">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Label
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Value
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Sort Order
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {memberTypes.map((type) => (
                <tr key={type.id}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                    {type.label}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                    {type.value}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                    {type.sort_order}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`px-2 py-1 rounded text-xs font-semibold ${
                        type.is_active
                          ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300"
                          : "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300"
                      }`}
                    >
                      {type.is_active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button
                      onClick={() => handleEdit(type)}
                      className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300 mr-4"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(type.id)}
                      className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create/Edit Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4 text-gray-900 dark:text-white">
              {editingType ? "Edit Member Type" : "Create Member Type"}
            </h2>
            <form onSubmit={handleSubmit}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    Label <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.label}
                    onChange={(e) => {
                      const newLabel = e.target.value;
                      setFormData({
                        ...formData,
                        label: newLabel,
                        value: editingType ? formData.value : generateSlug(newLabel), // Auto-generate value only when creating
                      });
                    }}
                    className="dark-input w-full px-4 py-2.5"
                    placeholder="e.g., Senior Pastor, New Member"
                    required
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Value will be auto-generated from label
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    Value <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.value}
                    onChange={(e) => {
                      // Only allow manual editing if creating new (not editing)
                      if (!editingType) {
                        setFormData({ ...formData, value: e.target.value });
                      }
                    }}
                    className="dark-input w-full px-4 py-2.5"
                    placeholder="e.g., senior-pastor, new-member"
                    required
                    disabled={!!editingType}
                  />
                  {editingType ? (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Value cannot be changed after creation
                    </p>
                  ) : (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Auto-generated from label. You can edit if needed.
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    Sort Order
                  </label>
                  <input
                    type="number"
                    value={formData.sort_order}
                    onChange={(e) =>
                      setFormData({ ...formData, sort_order: parseInt(e.target.value) || 0 })
                    }
                    className="dark-input w-full px-4 py-2.5"
                    min="0"
                  />
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="is_active"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                  />
                  <label
                    htmlFor="is_active"
                    className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300"
                  >
                    Active
                  </label>
                </div>
              </div>

              <div className="flex justify-end gap-2 mt-6">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
                >
                  Cancel
                </button>
                <button type="submit" className="btn-primary px-4 py-2">
                  {editingType ? "Update" : "Create"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4 text-gray-900 dark:text-white">Delete Member Type</h2>
            <p className="mb-6 text-gray-600 dark:text-gray-300">
              Are you sure you want to delete this member type? This will deactivate it. This action cannot be undone.
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeleteTypeId(null);
                }}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MemberTypeManagement;
